import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { LsqDataSource } from '@lsq-frontend/angular-shared/api-service';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import {
    GenerateAccountSummaryReportRequest,
    GenerateReportRequest
} from '@lsq-frontend/remote-services/enterprise/common';
import {
    GenerateAccountSummaryReportService,
    GetAvailabilityCalculationRequest,
    GetAvailabilityCalculationResponse,
    GetAvailabilityCalculationService,
    GetFundsOutstandingBalanceRequest,
    GetFundsOutstandingBalanceResponse,
    GetFundsOutstandingBalanceService
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import {
    Color,
    DatePickerConfiguration,
    ErrorMessage,
    LifecycleAware,
    Utilities
} from '@lsq-frontend/common-ui';
import { FeatureFlagService, Icons, Serializer } from '@lsq-frontend/common-ui';
import { combineLatest, ReplaySubject } from 'rxjs';
import { AnalyticsValue } from '../../../constants/analytics.constants';
import { ReportsUserStateTypes } from '../reports.user-state-type';
import { AnalyticsService } from '@lsq-frontend/angular-shared/features/analytics';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';

@LifecycleAware()
@Component({
    selector: 'available-funds-summary',
    templateUrl: './available-funds-summary.component.html',
    styleUrls: ['./available-funds-summary.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AvailableFundsSummaryComponent implements OnDestroy, OnInit {
    public userStateType = ReportsUserStateTypes.AvailableFundsSummary;
    public savedDateFilterName = null;
    public datePickerConfiguration: DatePickerConfiguration;
    public title = 'Available Funds Summary';
    public analyticsName = AnalyticsValue.AvailableFundsSummary;
    public color = Color;
    public icons = Icons;
    public lastUpdatedDate = null;
    public dataSourceErrorCopy = ErrorMessage.dataSourceError;
    public isLoading = true;

    public availableFundsDataSource = new LsqDataSource({
        data$: this.getAvailabilityCalculationService.dataStream,
        error$: this.getAvailabilityCalculationService.errorStream,
        loading$: this.getAvailabilityCalculationService.isInFlightStream
    });
    public availableFundsData$ = this.availableFundsDataSource.connect();
    public fundsOutstandingBalanceDataSource = new LsqDataSource({
        data$: this.getFundsOutstandingBalanceService.dataStream,
        error$: this.getFundsOutstandingBalanceService.errorStream,
        loading$: this.getFundsOutstandingBalanceService.isInFlightStream
    });
    public fundsOutstandingBalanceData$ = this.fundsOutstandingBalanceDataSource.connect();

    public generateAccountSummaryReportRequestSubject = new ReplaySubject<
        GenerateAccountSummaryReportRequest
    >(1);
    public generateAccountSummaryReportRequestStream = this.generateAccountSummaryReportRequestSubject.asObservable();

    public totalPendingAmount = null;

    constructor(
        public getAvailabilityCalculationService: GetAvailabilityCalculationService,
        public getFundsOutstandingBalanceService: GetFundsOutstandingBalanceService,
        public reportService: GenerateAccountSummaryReportService,
        public serializer: Serializer,
        private _featureFlagService: FeatureFlagService,
        public _analyticsService: AnalyticsService,
        private _userService: UserService
    ) {}

    public ngOnInit(): void {
        var asOfDateFilter = new Date();
        this.configureDatePicker(asOfDateFilter);
        this.reportService.preventExecutionViaRequestStream();
        this.reportService.setRequestStreamAndAutoExecute(
            this.generateAccountSummaryReportRequestStream
        );

        combineLatest(this.availableFundsData$, this.fundsOutstandingBalanceData$).subscribe(
            ([availableFundsData, fundsOutstandingBalanceData]) => {
                this.totalPendingAmount = availableFundsData.totalPendingAmount;
                this._setLastUpdatedDate(availableFundsData, fundsOutstandingBalanceData);
            }
        );

        this.getAvailabilityCalculationService.execute(
            new GetAvailabilityCalculationRequest({ asOfDateFilter })
        );
        this.getFundsOutstandingBalanceService.execute(
            new GetFundsOutstandingBalanceRequest({ asOfDateFilter })
        );

        this.isLoading = false;
    }

    public configureDatePicker(initialDateFilter: Date): void {
        if (this._featureFlagService.isOnSync(FeatureFlags.HistoricalAvailabilityReport)) {
            this.datePickerConfiguration = new DatePickerConfiguration();
            this.datePickerConfiguration.model = initialDateFilter;
            this.setExportRequest(initialDateFilter);
            this.datePickerConfiguration.modelChange = this.handleDatePickerChange.bind(this);
        }
    }

    public handleDatePickerChange(asOfDateFilter: Date): void {
        this.isLoading = true;
        this._analyticsService.triggerSingleDateFilterEvent(
            this.analyticsName,
            asOfDateFilter as Date
        );

        this.getAvailabilityCalculationService.execute(
            new GetAvailabilityCalculationRequest({ asOfDateFilter })
        );
        this.getFundsOutstandingBalanceService.execute(
            new GetFundsOutstandingBalanceRequest({ asOfDateFilter })
        );

        this.setExportRequest(asOfDateFilter);

        this.isLoading = false;
    }

    private setExportRequest(asOfDateFilter: Date) {
        this.generateAccountSummaryReportRequestSubject.next(
            new GenerateAccountSummaryReportRequest({
                includePendingFunds: true,
                endpointName: this.reportService.endpointName,
                jsonRequest: this.serializer.serialize(
                    new GetAvailabilityCalculationRequest({
                        asOfDateFilter: asOfDateFilter,
                        companyKey: this._userService.getCompanyKey()
                    })
                )
            })
        );
    }

    public ngOnDestroy(): void {
        if (this.availableFundsDataSource) {
            this.availableFundsDataSource.disconnect();
        }
        if (this.fundsOutstandingBalanceDataSource) {
            this.fundsOutstandingBalanceDataSource.disconnect();
        }
    }

    private _setLastUpdatedDate(
        availableFundsData: GetAvailabilityCalculationResponse,
        fundsOutstandingData: GetFundsOutstandingBalanceResponse
    ): void {
        if (availableFundsData.lastUpdatedDate < fundsOutstandingData.lastUpdatedDate) {
            this.lastUpdatedDate = availableFundsData.lastUpdatedDate;
        } else {
            this.lastUpdatedDate = fundsOutstandingData.lastUpdatedDate;
        }
    }
}
